/**
 * Error Context Types
 * Type definitions for the global error handling system
 */

import { createContext } from 'react';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface ErrorDetails {
  id: string;
  message: string;
  severity: ErrorSeverity;
  timestamp: Date;
  code?: string;
  details?: any;
  suggestion?: string;
  action?: {
    label: string;
    handler: () => void;
  };
}

export interface ErrorContextValue {
  errors: ErrorDetails[];
  addError: (error: Omit<ErrorDetails, 'id' | 'timestamp'>) => void;
  removeError: (id: string) => void;
  clearErrors: () => void;
  getErrorCount: (severity?: ErrorSeverity) => number;
  showError: (message: string) => void; // Backward compatibility
}

export interface ErrorProviderProps {
  children: React.ReactNode;
  maxErrors?: number;
  autoHideDuration?: number;
}

// Backward compatibility
export interface ErrorState {
  id: string;
  message: string;
}

export interface ErrorContextType {
  showError: (message: string) => void;
}

// Common error codes for consistency
export const ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',

  // Storage errors
  STORAGE_FULL: 'STORAGE_FULL',
  STORAGE_CORRUPT: 'STORAGE_CORRUPT',

  // Validation errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  REQUIRED_FIELD: 'REQUIRED_FIELD',

  // State errors
  STATE_CORRUPT: 'STATE_CORRUPT',
  STATE_MIGRATION_FAILED: 'STATE_MIGRATION_FAILED',

  // General errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// Helper type for error recovery suggestions
export interface ErrorRecoverySuggestion {
  code: ErrorCode;
  suggestion: string;
  action?: () => void;
}

// Standard error recovery suggestions
export const ERROR_RECOVERY_SUGGESTIONS: Record<ErrorCode, string> = {
  [ERROR_CODES.NETWORK_ERROR]: 'Check your internet connection and try again.',
  [ERROR_CODES.TIMEOUT_ERROR]: 'The operation took too long. Please try again.',
  [ERROR_CODES.STORAGE_FULL]: 'Browser storage is full. Clear some data and try again.',
  [ERROR_CODES.STORAGE_CORRUPT]: 'Storage data is corrupted. Try clearing your browser data.',
  [ERROR_CODES.VALIDATION_FAILED]: 'Please check your input and try again.',
  [ERROR_CODES.REQUIRED_FIELD]: 'Please fill in all required fields.',
  [ERROR_CODES.STATE_CORRUPT]: 'Application state is corrupted. Try refreshing the page.',
  [ERROR_CODES.STATE_MIGRATION_FAILED]: 'Failed to update app data. Please contact support.',
  [ERROR_CODES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
  [ERROR_CODES.PERMISSION_DENIED]: 'You do not have permission to perform this action.',
};

export const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);
